import type { CardAction, CardKind } from "../../../shared/types.ts";
import { colorValue } from "./color.ts";
import { parseDate } from "./dates.ts";
import type { ParsedQuery } from "./types.ts";

/**
 * Versão da gramática. Coleção automática guarda o texto da consulta e Space.queryVersion (carimbado pelo servidor com
 * QUERY_VERSION em src/domain/org.ts); os dois sobem juntos quando uma consulta salva passar a significar outra coisa.
 */
export const GRAMMAR_VERSION = 1;

/** Minúsculas e sem acento: "Ação" → "acao". Mesma dobra no índice e na consulta (só marcas latinas U+0300–036F). */
export const fold = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const KINDS: Record<string, CardKind> = {
  imagem: "image", image: "image", foto: "image", photo: "image", link: "link", site: "link", pagina: "link", page: "link",
  webpage: "link", nota: "note", note: "note", citacao: "quote", quote: "quote", destaque: "highlight", highlight: "highlight",
  cor: "color", color: "color", paleta: "color", palette: "color", pdf: "pdf", video: "video", audio: "audio", arquivo: "file", file: "file",
};
// Entidades Schema.org mais comuns (mesma lista do servidor, src/search/query.ts).
const ENTITIES: Record<string, string> = {
  livro: "Book", book: "Book", receita: "Recipe", recipe: "Recipe", produto: "Product", product: "Product",
  artigo: "Article", article: "Article", filme: "Movie", movie: "Movie",
};
/** Entidade → palavra de tipo: em pt (facetas da busca: o chip vira tipo:<palavra>). */
export const ENTITY_WORD: Record<string, string> = { Book: "livro", Recipe: "receita", Product: "produto", Article: "artigo", Movie: "filme" };
const ACTIONS: Record<string, CardAction> = {
  ler: "ler", read: "ler", assistir: "assistir", watch: "assistir", ouvir: "ouvir", listen: "ouvir",
  comprar: "comprar", buy: "comprar", fazer: "fazer", do: "fazer",
};
const DONE: Record<string, boolean> = { sim: true, yes: true, true: true, nao: false, no: false, false: false };
const LANGS: Record<string, string> = {
  portugues: "pt", portuguese: "pt", ingles: "en", english: "en", espanhol: "es", spanish: "es",
  frances: "fr", french: "fr", alemao: "de", german: "de", italiano: "it", italian: "it",
};
const RANGE_KEYS = ["de", "from", "ate", "to"];
const TAG_KEYS = ["tag", "etiqueta"];
// Preposição que vai junto com a data: "fotos de março" filtra março, sem exigir a palavra "de".
const DATE_LEAD = new Set(["de", "do", "da", "em", "no", "na", "desde", "of", "from", "in", "on", "since"]);
// Mês em inglês que também é palavra comum: só é data sozinho ou com dia/ano ("may 2025", "march 1").
const AMBIGUOUS = new Set(["may", "march"]);
/** Etiqueta reservada (PRIVATE_TAG em src/domain/cards.ts): card com ela é privado; #privado na consulta o inclui. */
export const PRIVATE_TAG = "privado";

// "frase" / -"frase" / chave:"valor" / #"etiqueta longa" (aspas sem fechar vão até o fim), | , ou palavra solta.
const TOKEN = /(-?)(#|\p{L}+:)?"([^"]*)"?|\||[^\s|"]+/gu;
export const hasWord = (s: string) => /[\p{L}\p{N}]/u.test(s);
const host = (v: string) => v.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];

/** Tipo (formato do card) ou entidade de uma palavra, no singular ou no plural (imagens, citações, cores, livros). */
function typeOf(v: string): { kind?: CardKind; entity?: string } | null {
  for (const w of [v, v.replace(/s$/, ""), v.replace(/ns$/, "m"), v.replace(/oes$/, "ao"), v.replace(/es$/, "")]) {
    if (Object.hasOwn(KINDS, w)) return { kind: KINDS[w] };
    if (Object.hasOwn(ENTITIES, w)) return { entity: ENTITIES[w] };
  }
  return null;
}
/** Formato de uma palavra solta, no singular ou no plural ("nota", "imagens", "vídeos"); assunto ("receitas") não. */
const kindWord = (w: string) => typeOf(w)?.kind;

/** Operador chave:valor (valor já dobrado). Valor incompleto (ainda digitando) não filtra. false = chave desconhecida. */
function keyed(q: ParsedQuery, k: string, v: string): boolean {
  switch (k) {
    case "tipo": case "type": case "formato": case "format": {
      if (!hasWord(v)) return true;
      const t = typeOf(v);
      if (t?.kind) q.kinds.push(t.kind);
      else if (t?.entity) q.entities.push(t.entity);
      else q.unknownTypes.push(v); // tipo desconhecido não casa nada (e a UI explica)
      return true;
    }
    case "tag": case "etiqueta": if (hasWord(v)) q.tags.push(v); return true;
    case "site": if (v) q.sites.push(host(v)); return true;
    case "texto": case "text": if (hasWord(v)) q.textOnly.push(v); return true;
    case "objeto": case "object": if (hasWord(v)) q.objects.push(v); return true;
    case "idioma": case "language": if (hasWord(v)) q.langs.push(Object.hasOwn(LANGS, v) ? LANGS[v] : v); return true;
    case "cor": case "color": {
      const c = colorValue(v);
      if (c) q.colors.push(c);
      return true;
    }
    case "em": case "in": if (hasWord(v)) q.spaces.push(v); return true;
    case "acao": case "action": if (Object.hasOwn(ACTIONS, v)) q.actions.push(ACTIONS[v]); return true;
    case "feito": case "done": case "completed": case "concluido": if (Object.hasOwn(DONE, v)) q.done = DONE[v]; return true;
  }
  return false;
}

/**
 * Gramática da §7.4: E, OU/|, -exclui, "frase", #etiqueta (tag:), tipo: (type:, formato:, format:), site:, texto:,
 * objeto:, idioma:, cor:, em:, acao:, feito: (completed:), datas, lixeira, #privado e "~" (tudo depois do primeiro "~" é
 * a descrição semântica, como no servidor). `colorWords`: cor solta ("azul", "blue") vira cor: — o motor só pede isso
 * quando o texto não achou nada (H52).
 */
export function parseQuery(raw: string, now = Date.now(), opts: { colorWords?: boolean; literal?: boolean } = {}): ParsedQuery {
  const q: ParsedQuery = {
    terms: [], anyOf: [], phrases: [], excludes: [], tags: [], kinds: [], entities: [], unknownTypes: [], sites: [], textOnly: [],
    objects: [], langs: [], colors: [], spaces: [], actions: [], done: null, date: null, trash: false, includePrivate: false,
    semantic: null, recent: false, raw,
  };
  const tilde = raw.indexOf("~");
  if (tilde >= 0) q.semantic = raw.slice(tilde + 1).trim().replace(/\s+/g, " ") || null;
  const groups: string[][] = [];
  const range: string[] = [];
  let afterTerm = false; // o token anterior foi um termo solto (OU só liga termos vizinhos)
  let or = false;
  // Último segmento (H84): etiqueta ou data ordenam do mais recente; texto, por relevância. Exclusão não conta.
  let last: "tag" | "date" | "text" | "other" = "other";
  let lastTerm = false; // o último segmento é um termo solto (pode ser o fim de uma data: "receita ontem")
  for (const [tok, neg, key, quoted] of fold(tilde >= 0 ? raw.slice(0, tilde) : raw).matchAll(TOKEN)) {
    const wasTerm: boolean = afterTerm;
    const joinPrev = or;
    afterTerm = or = false;
    if (quoted !== undefined) {
      const p = quoted.trim().replace(/\s+/g, " ");
      if (!hasWord(p)) continue;
      if (neg && key !== "#") {
        q.excludes.push(p);
        continue;
      }
      lastTerm = false;
      const k = key?.slice(0, -1);
      if (key === "#") (q.tags.push(p), (last = "tag"));
      else if (k && keyed(q, k, p)) last = TAG_KEYS.includes(k) ? "tag" : "other";
      else (q.phrases.push(p), (last = "text"));
      continue;
    }
    if (tok === "|" || tok === "ou" || tok === "or") {
      or = wasTerm;
      afterTerm = wasTerm;
      continue;
    }
    if (tok[0] === "-") {
      if (hasWord(tok)) q.excludes.push(tok.slice(1));
      continue;
    }
    lastTerm = false;
    if (tok[0] === "#") {
      if (tok.length > 1) (q.tags.push(tok.slice(1)), (last = "tag"));
      continue;
    }
    const kv = /^(\p{L}+):(.*)$/u.exec(tok);
    if (kv && RANGE_KEYS.includes(kv[1])) {
      range.push(tok);
      last = "date";
      continue;
    }
    if (kv && keyed(q, kv[1], kv[2])) {
      last = TAG_KEYS.includes(kv[1]) ? "tag" : "other";
      continue;
    }
    // ponytail: "lixeira" solto em qualquer posição liga o modo lixeira; para buscar a palavra, use "lixeira" entre aspas.
    if (tok === "lixeira" || tok === "trash") {
      q.trash = true;
      continue;
    }
    if (!hasWord(tok)) continue;
    if (joinPrev) groups[groups.length - 1].push(tok);
    else groups.push([tok]);
    afterTerm = true;
    last = "text";
    lastTerm = !joinPrev;
  }
  if (range.length) q.date = parseDate(range.join(" "), now); // inválido (ainda digitando) → sem filtro
  for (const g of groups) {
    if (g.length > 1) q.anyOf.push(g);
    else q.terms.push(g[0]);
  }
  // Data dentro do texto (D7): a maior sequência de termos que forma uma data vira filtro; o resto continua texto.
  // Ano ou mês soltos também contam (decisão O4: para buscar a palavra, use aspas). `literal`: só a data que é todo o
  // texto ("ontem"); o motor usa essa leitura quando a outra não achou nada ("notas fiscais de setembro" é um título).
  const t = q.terms;
  if (!q.date && t.length) {
    find: for (let len = Math.min(t.length, 6); len >= (opts.literal ? t.length : 1); len--) {
      for (let i = 0; i + len <= t.length; i++) {
        if (len === 1 && t.length > 1 && AMBIGUOUS.has(t[i])) continue;
        const d = parseDate(t.slice(i, i + len).join(" "), now);
        if (!d) continue;
        q.date = d;
        const from = i > 0 && DATE_LEAD.has(t[i - 1]) ? i - 1 : i;
        q.terms = [...t.slice(0, from), ...t.slice(i + len)];
        if (i + len === t.length && lastTerm) last = "date";
        break find;
      }
    }
  }
  // Tipo solto ("notas", "imagem", "fotos de março", "notas ou imagens") só vira filtro quando é todo o texto que sobra;
  // junto de outras palavras é palavra ("notas fiscais", "meeting notes", "cpu cores"): pode ser o título do card.
  const free = [...q.terms, ...q.anyOf.flat()];
  if (free.length && !q.phrases.length && free.every(kindWord)) {
    q.kinds.push(...new Set(free.map((w) => kindWord(w)!)));
    q.terms = [];
    q.anyOf = [];
    if (last === "text") last = "other";
  }
  if (opts.colorWords) {
    q.terms = q.terms.filter((w) => {
      const c = colorValue(w);
      if (c) q.colors.push(c);
      return !c;
    });
  }
  q.recent = last === "tag" || last === "date";
  q.includePrivate = q.tags.includes(PRIVATE_TAG);
  return q;
}

/** Termo solto que é nome de cor ("azul", "blue"): o motor tenta cor:<nome> quando o texto não acha nada (H52). */
export const isColorWord = (w: string) => colorValue(w) !== null;

/**
 * Texto para GET /v1/search?q= na gramática do servidor (src/search/query.ts): termos, frases, -exclusões, #etiquetas,
 * tipo:, site:, objeto:, idioma: (só a língua, "pt": o servidor compara o código com "=" sensível a maiúsculas) e
 * "~descrição". OU, cor:, em:, acao:, feito: e datas não vão: a UI os aplica com SearchResult.filterIds.
 * Sem "~", o texto livre vira a descrição (recurso semântico da §7.4 quando a busca local não acha nada; é o que
 * /v1/search/semantic faz, mas levando #privado e os filtros). null = nada a perguntar (lixeira, sem texto, ou tipo
 * desconhecido, que não casa nada).
 */
export function serverQuery(q: ParsedQuery): string | null {
  if (q.trash || (q.unknownTypes.length && !q.kinds.length && !q.entities.length)) return null;
  const quote = (s: string) => `"${s}"`;
  const free = [...q.terms, ...q.anyOf.flat(), ...q.phrases, ...q.textOnly].join(" ");
  const semantic = q.semantic ?? free;
  if (!hasWord(semantic)) return null;
  return [
    ...(q.semantic ? [...q.terms, ...[...q.phrases, ...q.textOnly].map(quote)] : []), // o servidor não tem OU
    ...q.objects.map((o) => (o.includes(" ") ? quote(o) : `objeto:${o}`)),
    ...q.excludes.map((x) => `-${quote(x)}`),
    ...q.tags.map((t) => `#${quote(t)}`),
    ...[...q.kinds, ...q.entities].map((k) => `tipo:${k.toLowerCase()}`),
    ...q.sites.map((s) => `site:${s}`),
    ...q.langs.map((l) => `idioma:${l.split("-")[0]}`),
    `~${semantic}`,
  ].join(" ");
}
